import { getUserIdFromLoginJwt, isLogedIn } from '@/modules/system/auth/application/auth.service';
import { getModuloRelatoriosAtividadesHabilitado } from '@/modules/system/configuracoes/modulo-relatorios-atividades-habilitado';
import MyContext from '@/shared/contexts/AppContext';
import {
  NAVIGATION_VIEW_LABELS,
  NavigationView,
} from '@/shared/constants/navigation-view.enum';
import {
  buildNavigationPermissions,
  type NavigationPermissions,
} from '@/shared/navigation/navigation-permissions.utils';
import {
  canSwitchNavigationView,
  applyDefensorDiretorView,
  inferNavigationView,
  resolveEffectiveNavigationView,
} from '@/shared/navigation/navigation-view.utils';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

export function useNavigationView() {
  const context = useContext(MyContext);
  if (!context) {
    throw new Error('useNavigationView deve ser usado dentro de MyProvider');
  }

  const { state, setState, sessionReady, authVisao, ehDefensorDiretor, assessorPodeEditarRelatorio } = context;
  const [relatoriosHabilitado, setRelatoriosHabilitado] = useState(true);

  const userId = state.userId ?? getUserIdFromLoginJwt() ?? null;

  useEffect(() => {
    void getModuloRelatoriosAtividadesHabilitado()
      .then(setRelatoriosHabilitado)
      .catch(() => setRelatoriosHabilitado(true));
  }, []);

  const permissions: NavigationPermissions = useMemo(
    () => buildNavigationPermissions(relatoriosHabilitado, ehDefensorDiretor, authVisao, assessorPodeEditarRelatorio),
    [relatoriosHabilitado, ehDefensorDiretor, authVisao, assessorPodeEditarRelatorio, sessionReady, state.username],
  );

  const viewReady = !isLogedIn() || sessionReady;

  const inferredView = useMemo((): NavigationView | null => {
    if (authVisao != null) {
      return applyDefensorDiretorView(authVisao, ehDefensorDiretor);
    }
    if (isLogedIn() && !sessionReady) return null;
    return inferNavigationView(
      permissions,
      { isCorregedoria: false, ehDefensorDiretor },
      userId,
    );
  }, [authVisao, sessionReady, permissions, ehDefensorDiretor, userId]);

  const effectiveView = useMemo((): NavigationView | null => {
    if (inferredView == null) return null;
    const usarOverrideSessao =
      state.navigationViewOverride != null &&
      !String(state.username ?? '').trim() &&
      userId != null;
    return resolveEffectiveNavigationView(
      inferredView,
      state.navigationViewOverride,
      userId,
      { usarOverrideSessao },
    );
  }, [inferredView, state.navigationViewOverride, state.username, userId]);

  const viewLabel =
    effectiveView != null ? NAVIGATION_VIEW_LABELS[effectiveView] : '';
  const podeAlternarVisao = canSwitchNavigationView(userId);

  const setNavigationViewOverride = useCallback(
    (view: NavigationView | undefined) => {
      setState({
        ...state,
        navigationViewOverride: view,
      });
    },
    [setState, state],
  );

  return {
    effectiveView,
    inferredView,
    viewLabel,
    viewReady,
    permissions,
    moduloRelatoriosAtividadesHabilitado: relatoriosHabilitado,
    podeAlternarVisao,
    userId,
    setNavigationViewOverride,
  };
}
